import React from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';


import { Text } from '../../../components';
import { getFullMachineName } from '../../../../common/utils/machine';
import { ROLE_PERMISSIONS, POS_TYPE } from '../../../../common/constants';
import { addressToString } from '../../../../common/utils/address';
import { useUserContext } from '../../../../common/utils/context';

import { Icon, TypographyParagraph } from '../../../components';
import { ErrorCallout } from '../../../components/callout';
import { MapLink } from '../../map/MapLink';
import { useTheme } from '../../../theme';

// A driver can be authorised for a docket while the direct machine query is
// route-scoped out. Keep the docket data as a read-only fallback in that case.
export const getMachineDirectionsParams = (machine, canSeeMachineDetail) => {
	const machineName = getFullMachineName(machine, machine.place, machine.location);

	return canSeeMachineDetail
		? {
			fallbackMachine: machine,
			machineId: machine.id,
			machineName,
		}
		: {
			machine,
			machineId: machine.id,
			machineName,
			forceReadOnly: true,
		};
};

const DocketLocationDetail = ({ docket, styles }) => {
	const navigation = useNavigation();
	const { hasPermission } = useUserContext();
	const { theme } = useTheme();
	const iconColor = theme.text2;

	const canSeeMachineDetail = hasPermission(ROLE_PERMISSIONS.VIEW_MACHINE_DETAIL_PAGE);
	const canCreateNotes = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_NOTES);

	const { machine } = docket;
	const isMarket = machine.type === POS_TYPE.MARKET;

	const machineName = getFullMachineName(machine, machine.place, machine.location);

	const goToMachineLocationTabsView = () => {
		navigation.push('MachineDetailTabView', {
			machineId: machine.id,
			machineName,
			locationId: machine.location?.id,
			isMarket,
			locationTab: true,
		});
	};

	const goToLocationNotes = () => {
		navigation.push('LocationNotes', {
			locationId: machine.location.id,
		});
	};

	const goToMachineDirections = () => {
		navigation.push('MachineDirectionsView', getMachineDirectionsParams(machine, canSeeMachineDetail));
	};

	const mapLocation = machine.location
		? {
			...machine.location,
			coordinates: machine.location.coordinates?.length ? machine.location.coordinates : machine.coordinates,
		}
		: (machine.coordinates?.length ? { coordinates: machine.coordinates } : null);
	const shouldShowMachineAddress = Boolean(mapLocation?.coordinates?.length || addressToString(mapLocation?.addressObj));

	const unimportantLocationNotes = machine.location
		? (machine.location.noteCount - (machine.location.importantNotes ? machine.location.importantNotes.length : 0)) : undefined;

	return (
		<View>
			<Text style={styles.headerText}>Location details</Text>

			<View style={{ flexDirection: 'column' }}>
				{
					shouldShowMachineAddress && (
						<View style={{ flex: 1 }}>
							<MapLink location={mapLocation} />
						</View>
					)
				}
				<View style={{ flexDirection: 'row', marginRight: 8, marginBottom: 4 }}>
					<View style={styles.icon}>
						<Icon name="directions" size={13} color={iconColor} style={{ marginTop: 4 }} />
					</View>
					<View style={{ flex: 1 }}>
						<TypographyParagraph
							style={styles.typographyParagraph}
							text="Directions"
							href={true}
							onPress={goToMachineDirections}
						/>
					</View>
				</View>
				{
					// The notes link is the only way into the location notes screen, so note-adders
					// keep the row even with zero notes; other users skip the noise-only zero row.
					Boolean(machine.location) && (Boolean(machine.location.noteCount) || canCreateNotes) && (
						<View style={{ flexDirection: 'row', marginRight: 8, marginBottom: 4 }}>
							<View style={styles.icon}>
								<Icon name="note" size={12} color={iconColor} style={{ marginTop: 3 }} />
							</View>
							<View style={{ flex: 1 }}>
								{
									Boolean(machine.location.importantNotes) && machine.location.importantNotes.map(importantNote => (
										<ErrorCallout
											key={`l_note_${importantNote.id}`}
											text={importantNote.description}
										/>
									))
								}
								{
									(unimportantLocationNotes > 0 || canCreateNotes) && (
										<TypographyParagraph
											style={styles.typographyParagraph}
											text={unimportantLocationNotes > 0
												? `${unimportantLocationNotes} ${Boolean(machine.location.importantNotes) && machine.location.importantNotes.length ? 'other ' : ''}location ${unimportantLocationNotes === 1 ? 'note' : 'notes'}`
												: 'Add location note'
											}
											href={true}
											onPress={goToLocationNotes}
										/>
									)
								}
							</View>
						</View>
					)
				}
				{
					Boolean(canSeeMachineDetail) && machine.location && (
						<View style={{ flexDirection: 'row', marginRight: 8 }}>
							<View style={styles.icon}>
								<Icon name="moreHorizontal" size={12} color={iconColor} style={{ marginTop: 3 }} />
							</View>
							<View style={{ flex: 1 }}>
								<TypographyParagraph style={styles.typographyParagraph} text="View all location details" href={true} onPress={goToMachineLocationTabsView} />
							</View>
						</View>
					) ||
					(
						<View style={{ flexDirection: 'row', marginRight: 8 }}>
							<View style={styles.icon}>
								<Icon name="moreHorizontal" size={12} color={iconColor} style={{ marginTop: 3 }} />
							</View>
							<View style={{ flex: 1 }}>
								<TypographyParagraph style={styles.typographyParagraph} text="No location assigned" />
							</View>
						</View>
					)
				}
			</View>
		</View>
	);
};

export default DocketLocationDetail;
